/* TDB Ingredient Master · editable rates with effective-from + approval gate
 *
 * Per Dipali's spec (20-May-2026):
 *  • All 97 ingredients listed · current rate · last change · pending flag
 *  • Click row → edit modal · new rate · reason (mandatory) · auto-preview pct
 *  • If pct ≤ ±20% → applies immediately · cascade message
 *  • If pct > ±20% → submits as pending · email+WA to Dipali · banner says so
 *  • "View affected SKUs" expands a list before commit
 *  • CEO sees a "Pending approvals" tab with approve/reject buttons
 *  • Roji sees her own pending status (read-only · waiting for CEO)
 *  • Britto sees the list (read-only) · no edit button
 */
import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Beaker, Search, Loader2, AlertTriangle, CheckCircle2, X, History,
  ChevronRight, Sparkles, Plus, Edit3, Eye, ShieldCheck, ShieldAlert,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const CATEGORIES = ['raw', 'packaging', 'consumable', 'non-food', 'non_food'];

const fmtRate = (r) => `₹${Number(r || 0).toFixed(2)}`;

const fmtPct = (p) => {
  if (p === null || p === undefined) return '';
  const sign = p > 0 ? '+' : '';
  return `${sign}${Number(p).toFixed(1)}%`;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};


export default function TDBIngredientMaster({ currentUser }) {
  const [ingredients, setIngredients] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');
  const [editingItem, setEditingItem] = useState(null);
  const [showAddNew, setShowAddNew] = useState(false);

  const email = (currentUser?.email || '').toLowerCase();
  const isCEO = currentUser?.role === 'ceo' || email === 'dipali@clubconcierge.in';
  const isRoji = email === 'roji.mathew@mindescapes.in';
  const isAditya = email === 'sikand.adi@gmail.com';
  const canEdit = isCEO || isRoji || isAditya;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get('/tdb-mis/ingredient-master'),
        api.get('/tdb-mis/ingredient-master/pending-approvals'),
      ]);
      setIngredients(a.data?.ingredients || []);
      setPending(b.data?.pending || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load ingredient master');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return ingredients.filter((it) => {
      if (filterStatus === 'active' && !it.active) return false;
      if (filterStatus === 'inactive' && it.active) return false;
      if (filterCat !== 'all' && it.category !== filterCat) return false;
      if (q && !it.item_name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [ingredients, filterCat, filterStatus, search]);

  const stats = useMemo(() => {
    const active = ingredients.filter((i) => i.active).length;
    return {
      total: ingredients.length,
      active,
      pending: pending.length,
      byCategory: ingredients.reduce((acc, it) => {
        if (!it.active) return acc;
        acc[it.category] = (acc[it.category] || 0) + 1;
        return acc;
      }, {}),
    };
  }, [ingredients, pending]);

  if (loading) {
    return (
      <div className="bg-white border rounded-2xl p-8 text-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
        Loading ingredient master…
      </div>
    );
  }

  return (
    <div data-testid="tdb-ingredient-master" className="space-y-4">
      {/* Header + tabs */}
      <div className="bg-white border rounded-2xl p-5">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Beaker className="w-5 h-5 text-violet-700" />
              <h2 className="text-base font-semibold text-slate-900">
                Ingredient Master · Rate Editor
              </h2>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-800 font-medium uppercase tracking-wider">
                v2 pack
              </span>
            </div>
            <p className="text-xs text-slate-500">
              Edit rates here — they cascade to every recipe and SKU instantly.
              {' '}Changes &gt; ±20% need Dipali's approval before going live.
            </p>
          </div>
          {canEdit && (
            <button
              data-testid="tdb-im-add-ingredient-btn"
              onClick={() => setShowAddNew(true)}
              className="text-xs px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800 flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" /> Add ingredient
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <Stat label="Active" value={stats.active} />
          <Stat label="Total" value={stats.total} />
          <Stat label="Pending approvals" value={stats.pending}
                accent={stats.pending > 0 ? 'amber' : 'slate'} />
          <Stat label="Categories" value={Object.keys(stats.byCategory).length} />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <TabBtn id="all" current={tab} onClick={setTab} label="All ingredients" count={stats.active} />
        <TabBtn id="approvals" current={tab} onClick={setTab}
                label="Pending approvals" count={stats.pending}
                accent={stats.pending > 0 ? 'amber' : 'slate'} />
      </div>

      {tab === 'all' && (
        <>
          <div className="flex flex-wrap gap-2 items-center bg-white border rounded-2xl p-3">
            <div className="flex-1 min-w-[180px] relative">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
              <input
                data-testid="tdb-im-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name…"
                className="w-full text-sm pl-9 pr-3 py-2 rounded-md border border-slate-200"
              />
            </div>
            <select
              data-testid="tdb-im-filter-category"
              value={filterCat}
              onChange={(e) => setFilterCat(e.target.value)}
              className="text-sm px-2 py-2 rounded-md border border-slate-200"
            >
              <option value="all">All categories</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              data-testid="tdb-im-filter-status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="text-sm px-2 py-2 rounded-md border border-slate-200"
            >
              <option value="active">Active only</option>
              <option value="inactive">Inactive only</option>
              <option value="all">All</option>
            </select>
            <span className="text-xs text-slate-500 ml-auto">
              {filtered.length} shown · {stats.active} active total
            </span>
          </div>

          <div className="bg-white border rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Ingredient</th>
                  <th className="text-left px-3 py-2">Category</th>
                  <th className="text-right px-3 py-2">Rate</th>
                  <th className="text-left px-3 py-2">Unit</th>
                  <th className="text-left px-3 py-2">Last changed</th>
                  <th className="text-right px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr
                    key={it.item_name}
                    data-testid={`tdb-im-row-${it.item_name.replace(/[^a-z0-9]/gi, '_')}`}
                    className={`border-t border-slate-100 ${it.pending_change ? 'bg-amber-50/40' : ''}`}
                  >
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{it.item_name}</span>
                        {!it.active && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600">inactive</span>
                        )}
                        {it.pending_change && (
                          <span
                            data-testid={`tdb-im-pending-badge-${it.item_name.replace(/[^a-z0-9]/gi, '_')}`}
                            className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-900 font-medium flex items-center gap-1"
                          >
                            <ShieldAlert className="w-3 h-3" />
                            Pending {fmtPct(it.pending_change.change_pct)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{it.category}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtRate(it.rate)}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">/{it.unit}</td>
                    <td className="px-3 py-2 text-slate-500 text-xs">
                      {fmtDate(it.rate_effective_from)}<br />
                      <span className="text-slate-400">{it.last_changed_by?.split('@')[0] || ''}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {canEdit ? (
                        <button
                          data-testid={`tdb-im-edit-${it.item_name.replace(/[^a-z0-9]/gi, '_')}`}
                          onClick={() => setEditingItem(it)}
                          className="text-xs px-2 py-1 rounded-md border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1"
                        >
                          <Edit3 className="w-3 h-3" /> Edit
                        </button>
                      ) : (
                        <span className="text-[10px] text-slate-400">read-only</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="text-center py-8 text-sm text-slate-500">
                No ingredients match.
              </div>
            )}
          </div>
        </>
      )}

      {tab === 'approvals' && (
        <PendingApprovalsTab
          pending={pending}
          isCEO={isCEO}
          onAction={load}
        />
      )}

      {editingItem && (
        <EditRateModal
          ingredient={editingItem}
          onClose={() => setEditingItem(null)}
          onSaved={() => { setEditingItem(null); load(); }}
          isCEO={isCEO}
        />
      )}

      {showAddNew && (
        <AddIngredientModal
          onClose={() => setShowAddNew(false)}
          onSaved={() => { setShowAddNew(false); load(); }}
        />
      )}
    </div>
  );
}


function Stat({ label, value, accent = 'slate' }) {
  const palette = {
    slate: 'bg-slate-50 text-slate-900 border-slate-200',
    amber: 'bg-amber-50 text-amber-900 border-amber-300',
  }[accent];
  return (
    <div className={`px-3 py-2 rounded-md border ${palette}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}


function TabBtn({ id, current, onClick, label, count, accent = 'slate' }) {
  const active = id === current;
  const palette = accent === 'amber'
    ? 'bg-amber-100 text-amber-900 border-amber-300'
    : 'bg-slate-100 text-slate-900 border-slate-300';
  return (
    <button
      data-testid={`tdb-im-tab-${id}`}
      type="button"
      onClick={() => onClick(id)}
      className={`relative px-3 py-2 text-sm font-medium ${active ? 'text-slate-900 border-b-2 border-violet-700' : 'text-slate-500 hover:text-slate-900'}`}
    >
      {label}
      {count > 0 && (
        <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full border ${palette}`}>
          {count}
        </span>
      )}
    </button>
  );
}


// ─────────────────────────────────────────────────────────────────
// Rate edit modal · live preview · affected SKUs · approval banner
// ─────────────────────────────────────────────────────────────────
function EditRateModal({ ingredient, onClose, onSaved, isCEO }) {
  const [newRate, setNewRate] = useState(String(ingredient.rate));
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showAffected, setShowAffected] = useState(false);
  const [affected, setAffected] = useState(null);
  const [forceApply, setForceApply] = useState(false);

  const oldRate = Number(ingredient.rate);
  const newRateN = Number(newRate);
  const validNum = Number.isFinite(newRateN) && newRateN >= 0;
  const changePct = useMemo(() => {
    if (!validNum || newRateN === oldRate) return 0;
    if (oldRate === 0) return newRateN > 0 ? 100 : 0;
    return (newRateN - oldRate) / oldRate * 100;
  }, [oldRate, newRateN, validNum]);
  const needsApproval = Math.abs(changePct) > 20;

  const loadAffected = useCallback(async () => {
    try {
      const r = await api.get(`/tdb-mis/ingredient-master/${encodeURIComponent(ingredient.item_name)}/affected-skus`);
      setAffected(r.data);
    } catch (e) {
      setAffected({ count: 0, skus: [] });
    }
  }, [ingredient.item_name]);

  useEffect(() => { loadAffected(); }, [loadAffected]);

  const submit = async () => {
    if (!validNum) {
      toast.error('Enter a valid non-negative number');
      return;
    }
    if (!reason.trim()) {
      toast.error('Reason is mandatory · what changed and why?');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        new_rate: newRateN,
        reason: reason.trim(),
      };
      if (forceApply && isCEO) body.force_apply = true;
      const r = await api.put(
        `/tdb-mis/ingredient-master/${encodeURIComponent(ingredient.item_name)}/rate`,
        body,
      );
      if (r.data?.status === 'pending_approval') {
        toast.success(
          `Pending Dipali's approval · email + WhatsApp sent`,
          { duration: 6000 },
        );
      } else if (r.data?.status === 'applied') {
        const n = r.data?.cascaded?.skus_recosted || 0;
        toast.success(`Rate updated · ${n} SKU${n !== 1 ? 's' : ''} recosted`);
      } else if (r.data?.status === 'noop') {
        toast.info('No change · current rate is the same');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="tdb-im-edit-modal"
      className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl max-w-xl w-full shadow-2xl border max-h-[88vh] overflow-y-auto">
        <div className="flex items-start justify-between p-4 border-b border-slate-100">
          <div>
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Edit3 className="w-4 h-4" /> {ingredient.item_name}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Current rate · <span className="font-mono">{fmtRate(oldRate)}/{ingredient.unit}</span>
              {' · category '}{ingredient.category}
              {' · last changed '}{fmtDate(ingredient.rate_effective_from)}
            </p>
          </div>
          <button data-testid="tdb-im-edit-close" onClick={onClose} className="text-slate-500 hover:text-slate-900">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-700">New rate (₹ per {ingredient.unit})</label>
            <input
              data-testid="tdb-im-edit-rate-input"
              type="number"
              step="0.01"
              value={newRate}
              onChange={(e) => setNewRate(e.target.value)}
              className="mt-1 w-full text-sm px-3 py-2 rounded-md border border-slate-300"
            />
            {validNum && newRateN !== oldRate && (
              <div className="mt-1 text-xs flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded ${needsApproval ? 'bg-amber-100 text-amber-900' : 'bg-emerald-100 text-emerald-900'}`}>
                  {fmtPct(changePct)} change
                </span>
                {needsApproval ? (
                  <span className="text-amber-900 font-medium flex items-center gap-1">
                    <ShieldAlert className="w-3 h-3" />
                    Above ±20% threshold · Dipali's approval needed
                  </span>
                ) : (
                  <span className="text-emerald-900 font-medium flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" />
                    Within ±20% · applies immediately
                  </span>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700">
              Reason <span className="text-red-600">*</span>
            </label>
            <textarea
              data-testid="tdb-im-edit-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Monsoon supply tightening · Bengaluru wholesale up ₹25/kg"
              rows={2}
              maxLength={500}
              className="mt-1 w-full text-sm px-3 py-2 rounded-md border border-slate-300"
            />
            <div className="text-[10px] text-slate-500 mt-0.5">
              {reason.length}/500 · this text goes into the audit log + Dipali's WhatsApp if approval needed
            </div>
          </div>

          {/* Affected SKUs preview */}
          <div className="rounded-md border border-slate-200 bg-slate-50/50 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-slate-700 flex items-center gap-1">
                <Eye className="w-3 h-3" /> {affected ? affected.count : '…'} SKUs will recost
              </span>
              {affected && affected.count > 0 && (
                <button
                  data-testid="tdb-im-view-affected-toggle"
                  type="button"
                  onClick={() => setShowAffected(!showAffected)}
                  className="text-xs text-violet-700 hover:text-violet-900 flex items-center gap-0.5"
                >
                  {showAffected ? 'Hide' : 'View affected SKUs'}
                  <ChevronRight className={`w-3 h-3 transition ${showAffected ? 'rotate-90' : ''}`} />
                </button>
              )}
            </div>
            {affected && affected.via?.length > 0 && (
              <div className="text-[11px] text-slate-600 mb-1">
                Via: {affected.via.join(' · ')}
                {affected.bases?.length > 0 && <> · Bases: {affected.bases.join(', ')}</>}
                {affected.flavours?.length > 0 && <> · Flavours: {affected.flavours.join(', ')}</>}
                {affected.archetypes?.length > 0 && <> · Archetypes: {affected.archetypes.join(', ')}</>}
              </div>
            )}
            {showAffected && affected?.skus?.length > 0 && (
              <div
                data-testid="tdb-im-affected-list"
                className="mt-2 max-h-44 overflow-y-auto rounded border border-slate-200 bg-white"
              >
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-100 text-slate-600 text-[10px] uppercase">
                    <tr>
                      <th className="text-left px-2 py-1">Product</th>
                      <th className="text-left px-2 py-1">Variant</th>
                      <th className="text-right px-2 py-1">Food ₹</th>
                      <th className="text-right px-2 py-1">Margin %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {affected.skus.slice(0, 80).map((s, i) => (
                      <tr key={s.shopify_variant_id || i} className="border-t border-slate-100">
                        <td className="px-2 py-1 text-slate-800">{s.product_title}</td>
                        <td className="px-2 py-1 text-slate-600">{s.variant_title}</td>
                        <td className="px-2 py-1 text-right font-mono">{fmtRate(s.food_cost)}</td>
                        <td className="px-2 py-1 text-right">{Number(s.margin_pct || 0).toFixed(1)}%</td>
                      </tr>
                    ))}
                    {affected.skus.length > 80 && (
                      <tr>
                        <td colSpan={4} className="text-center text-[10px] text-slate-500 py-1">
                          …and {affected.skus.length - 80} more
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {needsApproval && isCEO && (
            <label className="flex items-center gap-2 text-xs text-slate-700 bg-violet-50 rounded-md p-2 border border-violet-200">
              <input
                data-testid="tdb-im-force-apply"
                type="checkbox"
                checked={forceApply}
                onChange={(e) => setForceApply(e.target.checked)}
              />
              <span>CEO override · skip approval gate and apply now</span>
            </label>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-100">
          <button
            data-testid="tdb-im-edit-cancel"
            onClick={onClose}
            className="text-sm px-3 py-2 rounded-md border border-slate-300 bg-white hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            data-testid="tdb-im-edit-save"
            onClick={submit}
            disabled={submitting || !validNum || !reason.trim() || newRateN === oldRate}
            className="text-sm px-4 py-2 rounded-md bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50 flex items-center gap-1"
          >
            {submitting && <Loader2 className="w-3 h-3 animate-spin" />}
            {needsApproval && !(forceApply && isCEO)
              ? 'Submit for approval'
              : `Save · cascade to ${affected?.count || 0} SKU${affected?.count !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Pending approvals tab
// ─────────────────────────────────────────────────────────────────
function PendingApprovalsTab({ pending, isCEO, onAction }) {
  const [busyKey, setBusyKey] = useState('');
  const action = async (item, decision, reason = '') => {
    setBusyKey(`${decision}|${item.ingredient_name}`);
    try {
      const endpoint = decision === 'approve' ? 'approve' : 'reject';
      const body = decision === 'reject' ? { reason } : null;
      const url = `/tdb-mis/ingredient-master/${encodeURIComponent(item.ingredient_name)}/${endpoint}`;
      const r = body ? await api.post(url, body) : await api.post(url);
      if (decision === 'approve') {
        const n = r.data?.cascaded?.skus_recosted || 0;
        toast.success(`Approved · ${n} SKU${n !== 1 ? 's' : ''} recosted`);
      } else {
        toast.success('Rejected · master rate unchanged');
      }
      onAction();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      setBusyKey('');
    }
  };

  if (!pending.length) {
    return (
      <div className="bg-white border rounded-2xl p-8 text-center">
        <ShieldCheck className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
        <div className="text-sm text-slate-700 font-medium">No pending approvals</div>
        <div className="text-xs text-slate-500 mt-1">
          Rate changes &gt; ±20% from Roji or Aditya will show up here for your decision.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="bg-amber-50 border border-amber-300 rounded-2xl p-3">
        <div className="flex items-center gap-2 text-amber-900 text-sm font-medium">
          <AlertTriangle className="w-4 h-4" />
          {pending.length} rate change{pending.length === 1 ? '' : 's'} need
          {pending.length === 1 ? 's' : ''} your decision
        </div>
        <p className="text-xs text-amber-800 mt-1">
          These are above ±20% so they're held until you approve or reject.
        </p>
      </div>

      {pending.map((p) => (
        <div
          key={p.ingredient_name}
          data-testid={`tdb-im-pending-${p.ingredient_name.replace(/[^a-z0-9]/gi, '_')}`}
          className="bg-white border border-amber-200 rounded-2xl p-4"
        >
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <div className="text-base font-semibold text-slate-900">{p.ingredient_name}</div>
              <div className="text-xs text-slate-600 mt-0.5 font-mono">
                {fmtRate(p.previous_rate)}{p.unit ? `/${p.unit}` : ''}
                {' → '}
                <span className="font-semibold text-amber-900">{fmtRate(p.rate)}{p.unit ? `/${p.unit}` : ''}</span>
                {' · '}
                <span className={p.change_pct > 0 ? 'text-red-700' : 'text-emerald-700'}>
                  {fmtPct(p.change_pct)}
                </span>
              </div>
            </div>
            <div className="text-[10px] text-slate-500 text-right">
              proposed by<br />
              <span className="font-medium text-slate-700">{p.changed_by?.split('@')[0]}</span><br />
              {fmtDate(p.created_at)}
            </div>
          </div>
          <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded p-2 mb-3">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Reason · </span>
            {p.change_reason || '(no reason given)'}
          </div>
          {isCEO ? (
            <div className="flex items-center justify-end gap-2">
              <button
                data-testid={`tdb-im-pending-reject-${p.ingredient_name.replace(/[^a-z0-9]/gi, '_')}`}
                onClick={() => {
                  const reason = window.prompt(
                    `Reject ${p.ingredient_name} rate change?\n\nReason (e.g. "looks like a typo · should be ₹150 not ₹1500"):`,
                  );
                  if (reason !== null) action(p, 'reject', reason);
                }}
                disabled={!!busyKey}
                className="text-xs px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50"
              >
                Reject
              </button>
              <button
                data-testid={`tdb-im-pending-approve-${p.ingredient_name.replace(/[^a-z0-9]/gi, '_')}`}
                onClick={() => action(p, 'approve')}
                disabled={!!busyKey}
                className="text-xs px-3 py-1.5 rounded-md bg-emerald-700 text-white hover:bg-emerald-800 flex items-center gap-1"
              >
                {busyKey === `approve|${p.ingredient_name}` && <Loader2 className="w-3 h-3 animate-spin" />}
                Approve · cascade
              </button>
            </div>
          ) : (
            <div className="text-xs text-slate-500 text-right">
              Waiting for Dipali to approve / reject
            </div>
          )}
        </div>
      ))}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Add new ingredient
// ─────────────────────────────────────────────────────────────────
function AddIngredientModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    item_name: '', unit: 'kg', rate: '',
    category: 'raw', notes: '', track_daily: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post('/tdb-mis/ingredient-master', {
        ...form,
        rate: parseFloat(form.rate) || 0,
      });
      toast.success('Ingredient added');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="tdb-im-add-modal"
      className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <h3 className="text-base font-semibold text-slate-900">Add ingredient to master</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-700">Name</label>
            <input
              data-testid="tdb-im-add-name"
              type="text"
              value={form.item_name}
              onChange={(e) => setForm({ ...form, item_name: e.target.value })}
              placeholder="e.g. Saffron · Edible Gold Dust"
              maxLength={80}
              className="mt-1 w-full text-sm px-3 py-2 rounded-md border border-slate-300"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs font-medium text-slate-700">Category</label>
              <select
                data-testid="tdb-im-add-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="mt-1 w-full text-sm px-2 py-2 rounded-md border border-slate-300"
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Unit</label>
              <select
                data-testid="tdb-im-add-unit"
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                className="mt-1 w-full text-sm px-2 py-2 rounded-md border border-slate-300"
              >
                {['kg', 'piece', 'litre', 'metre', 'grams'].map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Rate ₹/unit</label>
              <input
                data-testid="tdb-im-add-rate"
                type="number"
                step="0.01"
                value={form.rate}
                onChange={(e) => setForm({ ...form, rate: e.target.value })}
                className="mt-1 w-full text-sm px-2 py-2 rounded-md border border-slate-300"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">Notes (optional)</label>
            <input
              data-testid="tdb-im-add-notes"
              type="text"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              maxLength={500}
              className="mt-1 w-full text-sm px-3 py-2 rounded-md border border-slate-300"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input
              data-testid="tdb-im-add-daily"
              type="checkbox"
              checked={form.track_daily}
              onChange={(e) => setForm({ ...form, track_daily: e.target.checked })}
            />
            Track this in Britto's daily list (high-velocity items only)
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-100">
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-md border border-slate-300">Cancel</button>
          <button
            data-testid="tdb-im-add-save"
            onClick={submit}
            disabled={!form.item_name.trim() || submitting}
            className="text-sm px-4 py-2 rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1"
          >
            {submitting && <Loader2 className="w-3 h-3 animate-spin" />}
            Add ingredient
          </button>
        </div>
      </div>
    </div>
  );
}
